import React, { useContext, useEffect } from 'react';

import { hot } from 'react-hot-loader/root';

import { TrackEventContext } from 'common/containers/EventContainer';
import ClickupLandingHelmet from 'common/helmets/ClickupLandingHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import UppercaseHeader from 'common/UppercaseHeader';
import LandingCaseStudyHeadingText from 'common/www/LandingCaseStudyHeadingText';
import Clickup1 from 'img/landing/clickup-1.webp';
import Clickup2 from 'img/landing/clickup-2.webp';
import Clickup3 from 'img/landing/clickup-3.webp';
import Clickup4 from 'img/landing/clickup-4.webp';
import Clickup5 from 'img/landing/clickup-5.webp';
import ClickupWordmark from 'img/landing/clickup-wordmark.webp';
import Zeb from 'img/landing/zeb.webp';

import 'css/components/www/_LandingCustomersPage.scss';

const ClickupLanding = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed ClickUp Case Study');
  }, [trackEvent]);

  return (
    <div className="landingClickup landingCustomersPage">
      <ClickupLandingHelmet />
      <div className="fullWidthContainer">
        <div className="header">
          <LandingCaseStudyHeadingText title="How ClickUp uses Canny to keep track of feedback from a rapidly growing user base" />
          <div className="headerImage">
            <div className="nametag">
              <div className="name">Zeb Evans</div>
              <div className="title">Founder, CEO at ClickUp</div>
            </div>
            <LazyLoadedImage loading="eager" src={Zeb} alt="Zeb Evans" />
          </div>
        </div>
      </div>
      <div className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p>
              The project management software space is notoriously crowded. Amongst big players like
              Jira and Asana, ClickUp, who launched just over a year ago, is breaking through. Now
              with over 35,000 teams using ClickUp, they're just getting&nbsp;started.
            </p>
            <p>
              They're standing out by being hyper focused on creating an amazing
              user&nbsp;experience.
            </p>
            <p className="quote">
              &ldquo;To create a great product, there has to be continuous flow of communication and
              collaboration between product, operations, and customer&nbsp;success.&rdquo;
            </p>
            <h2 className="sectionTitle">Challenge</h2>
            <p>
              ClickUp's rapid growth presented challenges in handling inquiries from customers.
              Their existing solution was very manual and demanding of their customer
              success&nbsp;team.
            </p>
            <ul>
              <li>The product team needs to prioritize a large volume of&nbsp;feedback.</li>
              <li>
                The customer success team needs to continue the feedback loop. Interacting with
                users creates a sense of&nbsp;collaboration.
              </li>
              <li>Their users need a simple, effortless way to voice their&nbsp;feedback.</li>
            </ul>
            <h2 className="sectionTitle">Solution</h2>
            <p>
              The ClickUp team implemented Canny directly into their core product. When their users
              have feedback to give, Canny is only a click&nbsp;away.
            </p>
            <LazyLoadedImage className="border" src={Clickup1} />
            <p className="quote">
              &ldquo;Getting Canny set up couldn’t have been easier. We started with a blank canvas
              and allowed our users to paint the&nbsp;rest.&rdquo;
            </p>
            <p>
              Their main boards include "feature requests" and "integrations". All feedback gets
              tracked in Canny. Whether it came in directly, via email, or via&nbsp;Intercom.
            </p>
            <LazyLoadedImage className="border" src={Clickup2} />
            <p>
              The ClickUp team loves being able to add a vote on behalf of a user directly from an
              Intercom conversation. It's a great way to keep users informed.
            </p>
            <h2 className="sectionTitle">Results</h2>
            <p>
              Less than a year in, over 3,500 ClickUp users have given feedback over
              30,000&nbsp;times.
            </p>
            <p className="quote">
              &ldquo;Canny intrinsically provides us with a way of connecting and communicating with
              the people that matter most to our&nbsp;product.&rdquo;
            </p>
            <p>It removes bottlenecks and inefficiencies across several teams:</p>
            <p className="sectionSubheading">Product</p>
            <p>
              Zeb and the product team have managed to get unprecedented insight into the product
              they're building. Listening to their users has enabled them to improve the overall
              user experience—from creating and improving features to creating better
              user&nbsp;onboarding.
            </p>
            <p className="quote">
              &ldquo;Canny removes our need for user testing entirely, which is something I didn’t
              really&nbsp;anticipate.&rdquo;
            </p>
            <p>
              Zeb frequently follows up to get additional insight on a requested feature. Instead of
              sending all users a generic survey, he can ask a specific question to a very targeted
              set of users—everyone who expressed interest in a&nbsp;feature.
            </p>
            <LazyLoadedImage src={Clickup3} />
            <p className="sectionSubheading">Customer Success</p>
            <p>
              Zeb estimates that at least ~10% of support inquiries are related to feedback
              requests. One of their concerns was that Canny would be another thing to manage.
              However, they've found that, with Canny, they spend less effort managing feedback than
              they used&nbsp;to.
            </p>
            <p className="quote">
              &ldquo;The magic really happens when we can interact one-to-many with Canny. The
              feedback platform has been a life changer for customer&nbsp;success.&rdquo;
            </p>
            <LazyLoadedImage src={Clickup4} />
            <p className="sectionSubheading">ClickUp Users</p>
            <p>
              It's still rare to see a product team open up to their users and ClickUp users have
              noticed. ClickUp has built a very active community in Canny and while it’s still a
              small subset of overall users, it’s generally the segment that matters most. Users
              feel like their feedback actually gets taken seriously—because it&nbsp;does!
            </p>
            <div className="imageWithCaption">
              <a
                href="https://www.g2crowd.com/products/clickup/reviews/clickup-review-743629"
                rel="noopener"
                target="_blank">
                <LazyLoadedImage className="border" src={Clickup5} />
              </a>
              <p className="caption">Canny helps drive 5 star reviews</p>
            </div>
            <p className="quote">&ldquo;They LOVE&nbsp;it!&rdquo;</p>
          </div>
          <div className="right">
            <LazyLoadedImage
              alt="Clickup Wordmark"
              className="clickupWordmark"
              src={ClickupWordmark}
            />
            ClickUp is a productivity platform that provides a fundamentally new way to work. Fully
            customizable, ClickUp works for every type of team, so all teams can use the same app to
            plan, organize, and collaborate.
            <a href="https://clickup.com/" rel="noopener" target="_blank">
              clickup.com
            </a>
            <div className="stat">
              <UppercaseHeader>Customers</UppercaseHeader>
              <p>100,000+ teams</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Location</UppercaseHeader>
              <p>San Francisco, CA</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Using Canny since</UppercaseHeader>
              <p>2017</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Votes on Canny</UppercaseHeader>
              <p>180,000+</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded={true} />
      </div>
    </div>
  );
};
export default hot(ClickupLanding);
